import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import * as THREE from 'three';
import Footer from "../components/Footer";
import '../styles/IEarnGold.css';

function IEarnGold() {
    const [showModal, setShowModal] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);

    const containerRef = useRef(null);
    const canvasRef = useRef(null);

    const speedMultiplierRef = useRef(1.0);
    const isTransitioningRef = useRef(false);

    useEffect(() => {
        if (!canvasRef.current || !containerRef.current) return;

        const scene = new THREE.Scene();

        const width = containerRef.current.clientWidth;
        const height = containerRef.current.clientHeight;
        const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
        camera.position.z = 6;

        const renderer = new THREE.WebGLRenderer({
            canvas: canvasRef.current,
            antialias: true,
            alpha: true
        });
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        renderer.setSize(width, height);

        const clock = new THREE.Clock();

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
        scene.add(ambientLight);

        const blueLight = new THREE.PointLight(0x4d62e8, 2, 50);
        blueLight.position.set(5, 5, 5);
        scene.add(blueLight);

        const goldLight = new THREE.PointLight(0xd4af37, 2.5, 50);
        goldLight.position.set(-5, -5, 5);
        scene.add(goldLight);

        const globeGroup = new THREE.Group();
        scene.add(globeGroup);

        const mainGeom = new THREE.SphereGeometry(2, 18, 18);
        const wireframe = new THREE.WireframeGeometry(mainGeom);
        const globeLines = new THREE.LineSegments(wireframe, new THREE.LineBasicMaterial({
            color: 0x4d62e8,
            transparent: true,
            opacity: 0.38
        }));
        globeGroup.add(globeLines);

        const innerGeom = new THREE.SphereGeometry(1.85, 30, 30);
        const innerPointsMat = new THREE.PointsMaterial({
            color: 0xd4af37,
            size: 0.025,
            transparent: true,
            opacity: 0.65,
            sizeAttenuation: true
        });
        const innerPoints = new THREE.Points(innerGeom, innerPointsMat);
        globeGroup.add(innerPoints);

        const networkGroup = new THREE.Group();
        const nodeCount = 35;
        const nodeCoords = [];
        const nodeGroup = new THREE.Group();

        const nodeGeom = new THREE.SphereGeometry(0.035, 8, 8);
        const blueNodeMat = new THREE.MeshBasicMaterial({
            color: 0x4d62e8,
            transparent: true,
            opacity: 0.8
        });
        const goldNodeMat = new THREE.MeshBasicMaterial({
            color: 0xd4af37,
            transparent: true,
            opacity: 0.8
        });

        for (let i = 0; i < nodeCount; i++) {
            const u = Math.random();
            const v = Math.random();
            const theta = u * 2.0 * Math.PI;
            const phi = Math.acos(2.0 * v - 1.0);
            const r = 2.03;
            const x = r * Math.sin(phi) * Math.cos(theta);
            const y = r * Math.sin(phi) * Math.sin(theta);
            const z = r * Math.cos(phi);
            nodeCoords.push(x, y, z);

            const isGoldNode = Math.random() > 0.65;
            const nodeMesh = new THREE.Mesh(nodeGeom, isGoldNode ? goldNodeMat : blueNodeMat);
            nodeMesh.position.set(x, y, z);
            nodeGroup.add(nodeMesh);
        }
        networkGroup.add(nodeGroup);

        const lineCoords = [];
        for (let i = 0; i < nodeCount; i++) {
            for (let j = i + 1; j < nodeCount; j++) {
                const dx = nodeCoords[i * 3] - nodeCoords[j * 3];
                const dy = nodeCoords[i * 3 + 1] - nodeCoords[j * 3 + 1];
                const dz = nodeCoords[i * 3 + 2] - nodeCoords[j * 3 + 2];
                const dist = Math.sqrt(dx * dx + dy * dy + dz * dz);
                if (dist < 1.35) {
                    lineCoords.push(nodeCoords[i * 3], nodeCoords[i * 3 + 1], nodeCoords[i * 3 + 2]);
                    lineCoords.push(nodeCoords[j * 3], nodeCoords[j * 3 + 1], nodeCoords[j * 3 + 2]);
                }
            }
        }
        const lineGeom = new THREE.BufferGeometry();
        lineGeom.setAttribute('position', new THREE.Float32BufferAttribute(lineCoords, 3));
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x4d62e8,
            transparent: true,
            opacity: 0.22
        });
        const networkLines = new THREE.LineSegments(lineGeom, lineMaterial);
        networkGroup.add(networkLines);
        globeGroup.add(networkGroup);

        const orbitRadiusA = 2.75;
        const ringAGeom = new THREE.BufferGeometry();
        const verticesA = [];
        for (let i = 0; i <= 64; i++) {
            const theta = (i / 64) * Math.PI * 2;
            verticesA.push(Math.cos(theta) * orbitRadiusA, 0, Math.sin(theta) * orbitRadiusA);
        }
        ringAGeom.setAttribute('position', new THREE.Float32BufferAttribute(verticesA, 3));
        const ringAMat = new THREE.LineBasicMaterial({ color: 0x4d62e8, transparent: true, opacity: 0.2 });
        const ringA = new THREE.LineLoop(ringAGeom, ringAMat);
        globeGroup.add(ringA);

        const satAGeom = new THREE.SphereGeometry(0.055, 12, 12);
        const satAMat = new THREE.MeshBasicMaterial({ color: 0xd4af37 });
        const satA = new THREE.Mesh(satAGeom, satAMat);
        globeGroup.add(satA);

        const orbitRadiusB = 2.45;
        const ringBGeom = new THREE.BufferGeometry();
        const verticesB = [];
        for (let i = 0; i <= 64; i++) {
            const theta = (i / 64) * Math.PI * 2;
            const x = Math.cos(theta) * orbitRadiusB;
            const y = Math.sin(theta) * orbitRadiusB * Math.cos(Math.PI / 4);
            const z = Math.sin(theta) * orbitRadiusB * Math.sin(Math.PI / 4);
            verticesB.push(x, y, z);
        }
        ringBGeom.setAttribute('position', new THREE.Float32BufferAttribute(verticesB, 3));
        const ringBMat = new THREE.LineBasicMaterial({ color: 0xd4af37, transparent: true, opacity: 0.16 });
        const ringB = new THREE.LineLoop(ringBGeom, ringBMat);
        globeGroup.add(ringB);

        const satBGeom = new THREE.SphereGeometry(0.045, 12, 12);
        const satBMat = new THREE.MeshBasicMaterial({ color: 0x4d62e8 });
        const satB = new THREE.Mesh(satBGeom, satBMat);
        globeGroup.add(satB);

        const starsGeom = new THREE.BufferGeometry();
        const starsCount = 180;
        const starPositions = [];
        for (let i = 0; i < starsCount; i++) {
            starPositions.push((Math.random() - 0.5) * 45, (Math.random() - 0.5) * 45, (Math.random() - 0.5) * 45);
        }
        starsGeom.setAttribute('position', new THREE.Float32BufferAttribute(starPositions, 3));
        const starsMaterial = new THREE.PointsMaterial({
            color: 0xffffff,
            size: 0.04,
            transparent: true,
            opacity: 0.5,
            sizeAttenuation: true
        });
        const starField = new THREE.Points(starsGeom, starsMaterial);
        scene.add(starField);

        window.__threeCamera = camera;
        window.__threeGlobeGroup = globeGroup;

        let animationFrameId;
        const animate = () => {
            const time = clock.getElapsedTime();
            const mult = speedMultiplierRef.current;

            globeGroup.rotation.y += 0.0022 * mult;
            globeGroup.rotation.x += 0.0007 * mult;
            networkGroup.rotation.y -= 0.001 * mult;
            innerPoints.rotation.y += 0.0012 * mult;

            satA.position.x = Math.cos(time * 0.4) * orbitRadiusA;
            satA.position.z = Math.sin(time * 0.4) * orbitRadiusA;
            satA.position.y = 0;

            satB.position.x = Math.cos(time * -0.55) * orbitRadiusB;
            satB.position.y = Math.sin(time * -0.55) * orbitRadiusB * Math.cos(Math.PI / 4);
            satB.position.z = Math.sin(time * -0.55) * orbitRadiusB * Math.sin(Math.PI / 4);

            renderer.render(scene, camera);
            animationFrameId = requestAnimationFrame(animate);
        };

        animate();

        const handleResize = () => {
            if (!containerRef.current) return;
            const w = containerRef.current.clientWidth;
            const h = containerRef.current.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
            cancelAnimationFrame(animationFrameId);

            mainGeom.dispose();
            wireframe.dispose();
            innerGeom.dispose();
            nodeGeom.dispose();
            lineGeom.dispose();
            ringAGeom.dispose();
            satAGeom.dispose();
            ringBGeom.dispose();
            satBGeom.dispose();
            starsGeom.dispose();

            globeLines.material.dispose();
            innerPointsMat.dispose();
            lineMaterial.dispose();
            blueNodeMat.dispose();
            goldNodeMat.dispose();
            ringAMat.dispose();
            satAMat.dispose();
            ringBMat.dispose();
            satBMat.dispose();
            starsMaterial.dispose();

            renderer.dispose();
        };
    }, []);

    const handleConnectClick = () => {
        if (isConnecting) return;
        setShowModal(true);
    };

    const handleCancel = () => {
        setShowModal(false);
    };

    const handleGo = () => {
        setShowModal(false);
        setIsConnecting(true);
        isTransitioningRef.current = true;

        // 1. Fade and translate UI out
        gsap.to('.gateway-ui', {
            opacity: 0,
            y: -50,
            scale: 0.9,
            duration: 0.9,
            ease: 'power2.inOut'
        });

        const camera = window.__threeCamera;
        const globeGroup = window.__threeGlobeGroup;

        if (camera && globeGroup) {
            // 2. Zoom camera into center of globe
            gsap.to(camera.position, {
                z: 0.05,
                duration: 1.8,
                ease: 'power3.in'
            });

            // 3. Twist camera (3D spiral warp effect)
            gsap.to(camera.rotation, {
                z: Math.PI * 0.8,
                duration: 1.8,
                ease: 'power3.in'
            });

            // 4. Speed up particle and grid rotation
            gsap.to(speedMultiplierRef, {
                current: 35.0,
                duration: 1.8,
                ease: 'power3.in'
            });

            // 5. Expand globe size
            gsap.to(globeGroup.scale, {
                x: 5.5,
                y: 5.5,
                z: 5.5,
                duration: 1.8,
                ease: 'power3.in'
            });
        }

        // 6. Apply CSS scale and radial motion blur to canvas container
        gsap.to('.globe-canvas-container', {
            filter: 'blur(24px)',
            scale: 1.6,
            duration: 1.8,
            ease: 'power3.in'
        });

        // 7. Flash screen transition right before redirection
        gsap.to('.portal-flash', {
            opacity: 1,
            duration: 0.6,
            delay: 1.2,
            ease: 'power2.in',
            onComplete: () => {
                window.location.href = "https://dapp.ryonasset.com";
            }
        });
    };

    return (
        <div className='main-wrapper'>
            <main className="i-earn-gold-main">
                <div className="i-earn-gold-container" ref={containerRef}>
                    <div className="globe-canvas-container">
                        <canvas ref={canvasRef} />
                    </div>

                    <div className="gateway-ui">
                        <h3 className="gateway-title" style={{ padding: 0 }}>Connect to the RYON ASSET dApp</h3>

                        <button
                            className="gateway-connect-btn"
                            onClick={handleConnectClick}
                            disabled={isConnecting}
                        >
                            {isConnecting ? "Connecting..." : "Connect to dApp"}
                        </button>
                    </div>
                </div>

                <div className={`portal-modal-overlay ${showModal ? 'show' : ''}`}>
                    <div className="portal-modal">
                        <p className="portal-modal-eyebrow">I-EARN GOLD</p>
                        <h4>Connect to RYON ASSET dApp</h4>
                        <p>
                            You will be redirected to dapp.ryonasset.com. Please confirm that you want to switch to the Sepolia testnet prototype.
                        </p>
                        <div className="modal-action-buttons">
                            <button className="modal-action-btn modal-btn-go" onClick={handleGo}>
                                Go
                            </button>
                            <button className="modal-action-btn modal-btn-cancel" onClick={handleCancel}>
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>

                <div className="portal-flash" />
            </main>
            <Footer />
        </div>
    );
}

export default IEarnGold;
